import cx from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Button from 'plaid-threads/Button';
import IconButton from 'plaid-threads/IconButton';
import CloseS1 from 'plaid-threads/Icons/CloseS1';
import React, { useContext, useEffect, useState } from 'react';
import Image from 'src/components/docs/Image';
import useUser from 'src/contexts/docs/useUser';
import Context from '../../../contexts/docs';
import { CORE_EXCHANGE_DOCS_BASE_URL } from '../../core-exchange-docs/constants';
import { EXCHANGE_DOCS_BASE_URL } from '../../exchange-docs/constants';
import { DOCS_BASE_URL } from '../constants';
import {
  CREDIT_PRODUCTS,
  FRAUD_PRODUCTS,
  GENERAL,
  NavGroup,
  NavItem,
  ONBOARDING_PRODUCTS,
  PAYMENT_PRODUCTS,
  PFM_PRODUCTS,
} from '../constants/routeMap';
import APIVersionSwitcher from '../Navbar/APIVersionSwitcher';
import Search from '../Search';
import { getSection } from '../utilities';
import styles from './index.module.scss';
import SidebarNavGroup from './SidebarNavGroup';
import SidebarSubnav from './SidebarSubnav';

export interface Props {
  className?: string;
  mobile?: boolean;
  closed?: boolean;
  onClose?: () => void;
  navigation?: NavGroup; // optional override for reuse in finrise
  dark?: boolean; // override for finrise
  baseUrl?: string;
  logo?: string;
  headerText?: string | React.ReactNode;
  isCollapsible?: boolean;
}

const TOP_NAV: NavGroup = [
  ...GENERAL,
  ...PAYMENT_PRODUCTS,
  ...PFM_PRODUCTS,
  ...FRAUD_PRODUCTS,
  ...CREDIT_PRODUCTS,
  ...ONBOARDING_PRODUCTS,
];

const Sidebar: React.FC<Props> = (props: Props) => {
  const BASE_URL = props.baseUrl != null ? props.baseUrl : DOCS_BASE_URL;
  const router = useRouter();
  const [route, setRoute] = useState<NavItem>(
    getSection(router.pathname, BASE_URL, props.navigation),
  );

  // use Docs context
  const { tableOfContents, metadata } = useContext(Context);
  const { getFlagValue } = useUser();

  // on page-load & route-change - set the route section in states
  useEffect(() => {
    setRoute(getSection(router.pathname, BASE_URL, props.navigation));
  }, [BASE_URL, props.navigation, router.pathname]);

  if (props.mobile && props.closed) {
    return <></>;
  } else {
    return (
      <aside
        className={cx(
          props.className,
          styles.sidebar,
          styles.sidebarDefault,
          props.dark && styles.dark,
        )}
      >
        <nav aria-label='Main' className={styles.nav}>
          {props.mobile &&
            props.baseUrl !== EXCHANGE_DOCS_BASE_URL &&
            props.baseUrl !== CORE_EXCHANGE_DOCS_BASE_URL && (
              <div className={styles.mobileHeader}>
                <div className={styles.header}>
                  <div className={styles.homeLink}>
                    <Link
                      href={BASE_URL}
                      rel='noopener noreferrer'
                      className={styles.logo}
                    >
                      <Image
                        className={styles.logoImage}
                        src={'/assets/img/navbar/logo.svg'}
                        alt='Plaid logo'
                        width='80'
                        height='30.47'
                        noMarginBottom
                      />
                      <span className={styles.logoText}>Docs</span>
                    </Link>
                  </div>
                  <div className={styles.close}>
                    <IconButton
                      accessibilityLabel='Close nav'
                      icon={<CloseS1 />}
                      onClick={() => props.onClose()}
                    />
                  </div>
                </div>
                <div className={styles.searchAndVersion}>
                  <div className={styles.search}>
                    <Search />
                  </div>
                  <div className={styles.apiVersion}>
                    <APIVersionSwitcher />
                  </div>
                  <div className={styles.plaidDotCom}>
                    <Link href='/' className={styles.link}>
                      Plaid.com
                    </Link>
                  </div>
                </div>
              </div>
            )}

          {props.mobile &&
            (props.baseUrl === EXCHANGE_DOCS_BASE_URL ||
              props.baseUrl === CORE_EXCHANGE_DOCS_BASE_URL) && (
              <div className={styles.exchangeClose}>
                <IconButton
                  accessibilityLabel='Close nav'
                  icon={<CloseS1 />}
                  onClick={() => props.onClose()}
                />
              </div>
            )}
          {!props.mobile && (
            <div className={styles.header}>
              <Link
                href={BASE_URL}
                rel='noopener noreferrer'
                className={styles.logo}
              >
                <Image
                  className={styles.logoImage}
                  src={
                    props.logo != null
                      ? props.logo
                      : '/assets/img/navbar/logo.svg'
                  }
                  alt='Plaid logo'
                  width='80'
                  height='30.47'
                  noMarginBottom
                />
                <span className={styles.logoText}>
                  {props.headerText != null ? props.headerText : 'Docs'}
                </span>
              </Link>
            </div>
          )}
          <div className={styles.shrinkContainer}>
            {/* if route has children (subnav) or there is a table of contents in route, show the subnav or table of contents;
          otherwise, show nav */}
            {tableOfContents == null ||
            (metadata.flag && !getFlagValue(metadata.flag)) ? (
              <></>
            ) : (route?.children != null && route?.subNav !== false) ||
              (tableOfContents.length > 0 && route?.subNav !== false) ? (
              <SidebarSubnav
                mobile={props.mobile}
                onClose={props.onClose}
                currentRoute={route}
                baseUrl={BASE_URL}
              />
            ) : (
              <>
                <SidebarNavGroup
                  baseUrl={BASE_URL}
                  dark={props.dark}
                  navGroup={
                    props.navigation != null ? props.navigation : TOP_NAV
                  }
                  currentRoute={route}
                  isCollapsible={
                    props.isCollapsible ?? props.navigation != null
                  }
                />
              </>
            )}
            {props.mobile &&
              props.baseUrl !== EXCHANGE_DOCS_BASE_URL &&
              props.baseUrl !== CORE_EXCHANGE_DOCS_BASE_URL && (
                <>
                  <div className={styles.buttonWrapperAPIKeys}>
                    <Button
                      href='https://dashboard.plaid.com/signup'
                      target='_blank'
                      className={cx('signup-attach-utm', styles.getAPIKeys)}
                      secondary
                    >
                      Get API keys
                    </Button>
                  </div>
                </>
              )}
          </div>
        </nav>
      </aside>
    );
  }
};

Sidebar.displayName = 'Sidebar';
export default Sidebar;
